import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

/* components (kept as regular imports) */
import LoginPageIsLogedIn from "../components/loginProtected";
import ProtectedRoute from "../components/protectedRoute";
import Sidebar from "../components/thememaker/sidebar";
import NetworkStatusHandler from "../components/NetworkStatusHandler";
import ScrollToTop from "../components/Scrolltotop/Scrolltotop";
import PlanExpiryGuard from "../components/subscription/PlanExpiryGuard";
import SupplierView from "../components/inventory/SupplierView";
import SupplierOverview from "../components/inventory/SupplierOverview";
import ProductView from "../components/inventory/ProductsView";
import Forms from "../components/FormManagement/FormManagement";
import OnlineAppointment from "../pages/BookingManagement/OnlineAppointment";
import ProductImport from "../pages/inventory/ProductImport";
import ProductImportPreview from "../pages/inventory/ProductImportPreview";
import ProductImportHistory from "../pages/inventory/ProductImportHistory";
import VetClinicManagementTabs from "../pages/services/vetClinicManagementTabs";
import GettingStarted from "../pages/GettingStarted";
import ReminderHistory from "../components/Reports/ReminderHistory/ReminderHistory";
import InvoicePreview from "../pages/Invoicetab/InvoicePreview";

/* =========================
   Lazy-loaded pages (only ../pages/*)
   — no other logic changed
========================= */
const Dashboard = lazy(() => import("../pages/dashboard"));
const NewDashboard = lazy(() => import("../components/Dashboard/MainDashboard"));
const PageNotFound = lazy(() => import("../pages/404"));
const Login = lazy(() => import("../pages/login"));
const Profile = lazy(() => import("../pages/profile"));
const Notification = lazy(() => import("../pages/Notifications/index.js"));
const ResetPassword = lazy(() => import("../pages/reset-password"));
const UpdatePassword = lazy(
  () => import("../pages/Update Password/UpdatePassword"),
);
// const Forgget = lazy(() => import("../pages/oldforget-password/Forgget"));
const ListServices = lazy(() => import("../pages/services/listServices"));
const Chat = lazy(() => import("../pages/chat/Chatfile"));
const ClientPets = lazy(() => import("../pages/client_pets/ClientPets"));
const NewCustomerForm = lazy(() => import("../pages/client_pets/NewCustomerForm"));
const MainPage = lazy(() => import("../pages/online_profile/MainPage"));
const PaymentDetails = lazy(() => import("../pages/checkout/paymentDetails"));
const Thankyou = lazy(() => import("../pages/checkout/thankyou"));
const ClientChat = lazy(() => import("../pages/client_pets/Chatfile"));
const AdminDashboard = lazy(() => import("../pages/admin/dashboard"));
const HowToImport = lazy(() => import("../pages/client_pets/howToImport"));
const TakeGoogleData = lazy(
  () => import("../pages/client_pets/takeGoogleData"),
);
const CustomerList = lazy(() => import("../pages/admin/customer/CustomerList"));
const ViewCustomer = lazy(() => import("../pages/admin/customer/ViewCustomer"));
const UpdateCustomer = lazy(
  () => import("../pages/admin/customer/UpdateCustomer"),
);
const BookingList = lazy(() => import("../pages/admin/booking/Bookinglist"));
const TakeExcelData = lazy(() => import("../pages/client_pets/takeExcelData"));
const ImportHistory = lazy(() => import("../pages/client_pets/importHistory"));
const VendorRegistration = lazy(() => import("../pages/vendorRegistration"));
const ViewBooking = lazy(() => import("../pages/admin/booking/ViewBooking"));
const Register = lazy(() => import("../pages/Register/Register"));
const BookDemo = lazy(() => import("../pages/BookDemo/BookDemo"));
const FreeTrial = lazy(() => import("../pages/FreeTrial/FreeTrial"));
const BusinessRegistration = lazy(
  () => import("../pages/BusinessRegistration/BusinessRegistration"),
);
const Addservice = lazy(() => import("../pages/Addservice/Addservice"));
const Pricingplans = lazy(() => import("../pages/Pricingplans/Pricingplans"));
const ConsentForm = lazy(() => import("../pages/ConsentForm"));
const ConsentFormPublic = lazy(() => import("../pages/ConsentFormPublic"));
const NewCalender = lazy(
  () => import("../pages/Calendar/StaffBigCalendar/NewCalender"),
);
const CalendarOption = lazy(
  () => import("../pages/Calendar/BigCalendar/TabPages/CalendarOption"),
);
const EmployeeOrder = lazy(
  () => import("../pages/Calendar/BigCalendar/TabPages/EmployeeOrder"),
);
const AppointmentServices = lazy(
  () => import("../pages/Calendar/BigCalendar/TabPages/AppointmentServices"),
);
const AppointmentType = lazy(
  () => import("../pages/Calendar/BigCalendar/TabPages/AppointmentType"),
);
const AvailabilityCalendar = lazy(
  () =>
    import("../pages/Calendar/BigCalendar/EmployeeManagement/AvailabilityCalendar"),
);
const Fetchmorerevenue = lazy(
  () => import("../pages/Fetchmorerevenue/Fetchmorerevenue"),
);
const Solutions = lazy(() => import("../pages/Solutions/Solutions"));
const Resourcespage = lazy(
  () => import("../pages/Resourcespage/Resourcespage"),
);
const Pricingpage = lazy(() => import("../pages/Pricingpage/Pricingpage"));
const Solutionprofessional = lazy(
  () => import("../pages/Solutionprofessional/Solutionprofessional"),
);
const Solveterinaryclinics = lazy(
  () => import("../pages/Solveterinaryclinics/Solveterinaryclinics"),
);
const Solutionpetshop = lazy(
  () => import("../pages/Solutionpetshop/Solutionpetshop"),
);
const Solutiongrooming = lazy(
  () => import("../pages/Solutiongrooming/Solutiongrooming"),
);
const Solutionpetboarding = lazy(
  () => import("../pages/Solutionpetboarding/Solutionpetboarding"),
);

const EmployeeForm = lazy(() => import("../pages/Employee/EmployeeForm"));
const Connectus = lazy(() => import("../pages/Connectus/Connectus"));
const SizeManagement = lazy(() => import("../pages/Settings/SizeManagement"));
const CoatManagement = lazy(() => import("../pages/Settings/CoatManagement"));
const EditProfile = lazy(() => import("../pages/EditProfile/EditProfile"));
const EditAddress = lazy(() => import("../pages/EditAddress/EditAddress"));
const Featurespage = lazy(() => import("../pages/Featurespage/Featurespage"));
const OrderLists = lazy(() => import("../pages/Orders/OrderLists"));
const InvoiceList = lazy(() => import("../pages/invoice/invoiceList"));
const InvoiceLink = lazy(() => import("../pages/Invoicetab/InvoiceLink"));
const Forgetpassword = lazy(
  () => import("../pages/Forgetpassword/Forgetpassword"),
);
const Contactus = lazy(() => import("../pages/Contactus/Contactus"));
const Contactsales = lazy(() => import("../pages/Contactsales/Contactsales"));
const Contactcustomer = lazy(
  () => import("../pages/Contactcustomer/Contactcustomer"),
);
const AddonServices = lazy(
  () => import("../pages/services/ServiceManagement/AddonServices"),
);
const ExcelDataPreview = lazy(
  () => import("../pages/client_pets/excelData-preview"),
);
const Profilepage = lazy(() => import("../pages/Profilepage/Profilepage"));
const Orders = lazy(() => import("../pages/Orders/Orders"));
const OrderManagementOrders = lazy(
  () => import("../pages/OrderManagement/Orders"),
);
const OrderListsDetail = lazy(() => import("../pages/Orders/OrderListsDetail"));
const Freelisting = lazy(() => import("../pages/Freelisting/Freelisting"));
const RoomServices = lazy(() => import("../pages/Rooms/RoomServices"));
const Freelistinglanding = lazy(
  () => import("../pages/Freelistinglanding/Freelistinglanding"),
);
const EmployeeList = lazy(() => import("../pages/Employee/EmployeeList"));
const EmployeeDetails = lazy(() => import("../pages/Employee/EmployeeDetails"));
const Freelistcustomer = lazy(
  () => import("../pages/Freelistcustomer/Freelistcustomer"),
);
const Upgradeplanpage = lazy(
  () => import("../pages/Upgradeplanpage/Upgradeplanpage"),
);
const PurchaseBills = lazy(() => import("../pages/inventory/PurchaseBills"));
const Suppliers = lazy(() => import("../pages/inventory/Suppliers"));
const Products = lazy(() => import("../pages/inventory/Products"));
const PurchaseBill = lazy(() => import("../pages/inventory/PurchaseBill"));
const ViewPurchaseBill = lazy(
  () => import("../pages/inventory/ViewPurchaseBill"),
);
const PromotionsAndCoupons = lazy(
  () => import("../pages/Settings/PromotionsAndCoupons"),
);
const ViewClient = lazy(() => import("../pages/client_pets/ViewClient"));
const Peviewpage = lazy(() => import("../pages/Peviewpage/Peviewpage"));
const Payments = lazy(() => import("../pages/OrderManagement/Payments"));
const ExpenseManagement = lazy(
  () => import("../pages/ExpenseManagement/ExpenseManagement"),
);
const ExpensePayments = lazy(
  () => import("../pages/ExpenseManagement/ExpensePayments"),
);
const FAQPage = lazy(() => import("../pages/FAQ/FAQPage"));
const EnquiryOrders = lazy(
  () => import("../pages/OrderManagement/EnquiryOrders"),
);
const ServiceInclusions = lazy(
  () => import("../pages/services/ServiceInclusions"),
);
const RoomList = lazy(() => import("../pages/Rooms/RoomList"));
const Faqs = lazy(() => import("../pages/FAQ/Faqs"));
const ServiceAmenities = lazy(
  () => import("../pages/Settings/serviceAmenities"),
);
const About = lazy(() => import("../pages/About/About"));
const Blog = lazy(() => import("../pages/Blog/Blog"));
const BlogInfo = lazy(() => import("../pages/Blog/BlogInfo"));
const PrivacyPolicy = lazy(
  () => import("../pages/PrivacyPolicy/PrivacyPolicy"),
);
const TermOfUse = lazy(() => import("../pages/TermOfUse/TermOfUse"));
const SalesOrder = lazy(() => import("../pages/Orders/SalesOrder"));
const DogWakingService = lazy(
  () => import("../pages/services/dogWalkingService"),
);
const VetClinicService = lazy(
  () => import("../pages/services/vetClinicServices"),
);
const ClientsSearch = lazy(() => import("../pages/client_pets/ClientsSearch"));
// @ts-ignore
const ClientDetailsPage = lazy(() => import("../pages/client_pets/ClientDetailsPage"));
const DayCareServices = lazy(() => import("../pages/services/DayCareServices"));
const StandardReportListing = lazy(
  () => import("../components/Reports/StandardReports/StandardReportListing"),
);
const Feedback = lazy(() => import("../components/Reports/Feedback/Feedback"));
const FeedbackForm = lazy(() => import("../components/Reports/Feedback/Feedbackform"));
const CustomReportsListing = lazy(
  () => import("../components/Reports/CustomReports/CustomReportListing"),
);
const StoreList = lazy(() => import("../pages/Store/StoreList"));
const POS = lazy(() => import("../pages/POS"));
const InvoicePage = lazy(() => import("../pages/Invoicetab/InvoiceList"));
const HomePage = lazy(() => import("../pages/home/HomePage"));
const ProfileSettings = lazy(() => import("../pages/Settings/ProfileSettings"));
const ListingBookings = lazy(
  () => import("../pages/BookingManagement/ListingBookings"),
);
const CalendarBookings = lazy(
  () => import("../pages/BookingManagement/CalendarBookings"),
);
const TodaysBoardingActivity = lazy(
  () => import("../pages/BookingManagement/TodaysBoardingActivity"),
);
const StaffProductAssignment = lazy(
  () => import("../pages/inventory/StaffProductAssignment"),
);
const DiscountList = lazy(
  () => import("../pages/Setting/PromotionAndDiscount/DiscountList"),
);
const DiscountCreate = lazy(
  () => import("../pages/Setting/PromotionAndDiscount/DiscountCreate"),
);

const CheckoutBookings = lazy(
  () => import("../pages/BookingManagement/CheckoutBookings"),
);

const StaffSchedular = lazy(
  () => import("../pages/StaffSchedulerPage"),
); 
const GroomingBookings = lazy(
  () => import("../pages/BookingManagement/GroomingBookings"),
);
const BoardingBookings = lazy(
  () => import("../pages/BookingManagement/BoardingBookings"),
);
const VetBookings = lazy(
  () => import("../pages/BookingManagement/VetBookings"),
);

/* routes array left unchanged */
const routes = [
  // {
  //   path: "/dashboard",
  //   component: <Dashboard />,
  // },
  {
    path: "/dashboard",
    component: <NewDashboard />,
  },
  {
    path: "/admin/staff-management/scheduler",
    component: <StaffSchedular />,
  },
  {
    path: "/admin/booking-management/grooming-bookings",
    component: <GroomingBookings />,
  },
  {
    path: "/admin/booking-management/boarding-bookings",
    component: <BoardingBookings />,
  },
  {
    path: "/admin/booking-management/vet-bookings",
    component: <VetBookings />,
  },
  // {
  //   path: "/admin/dashboard",
  //   component: <NewDashboard />,
  // },
  {
    path: "/landing-page",
    component: <Profilepage />,
  },
  {
    path: "/login",
    component: <Login />,
  },
  {
    path: "/admin/inventory-management/suppliers/:supplierId",
    component: <SupplierView />,
  },
  {
    path: "/vendorRegistration",
    component: <VendorRegistration />,
  },
  {
    path: "/calendar/availabilityCalendar",
    component: <AvailabilityCalendar />,
  },
  {
    path: "/newcalender/calendarOptions",
    component: <CalendarOption />,
  },
  {
    path: "/newcalender/employeeOrder",
    component: <EmployeeOrder />,
  },
  {
    path: "/newcalender/appointmentServices",
    component: <AppointmentServices />,
  },
  {
    path: "/newcalender/appointmentType",
    component: <AppointmentType />,
  },
  {
    path: "/registers",
    component: <Register />,
  },
  {
    path: "/Solutions",
    component: <Solutions />,
  },
  {
    path: "/resources",
    component: <Resourcespage />,
  },
  {
    path: "/features",
    component: <Featurespage />,
  },
  {
    path: "/:token",
    component: <Peviewpage />,
  },
  {
    path: "/EditProfile",
    component: <EditProfile />,
  },
  {
    path: "/EditAddress",
    component: <EditAddress />,
  },
  {
    path: "/free-listing",
    component: <Freelisting />,
  },
  {
    path: "/freelistinglanding",
    component: <Freelistinglanding />,
  },
  {
    path: "/calendar",
    component: <NewCalender />,
  },
  {
    path: "/",
    component: <Fetchmorerevenue />,
  },
  {
    path: "/kennel-boarding-software",
    component: <Solutionprofessional />,
  },
  {
    path: "/pet-grooming-salon",
    component: <Solveterinaryclinics />,
  },
  {
    path: "/pet-shop",
    component: <Solutionpetshop />,
  },
  {
    path: "/mobile-grooming-van",
    component: <Solutiongrooming />,
  },
  {
    path: "/pet-veterinary-clinic",
    component: <Solutionpetboarding />,
  },
  {
    path: "/profile",
    component: <Profile />,
  },
  {
    path: "/about",
    component: <About />,
  },
  {
    path: "/blog",
    component: <Blog />,
  },
  {
    path: "/blog/:slug",
    component: <BlogInfo />,
  },
  {
    path: "/privacy-policy",
    component: <PrivacyPolicy />,
  },
  {
    path: "/term",
    component: <TermOfUse />,
  },
  {
    path: "/reset-password/:id",
    component: <ResetPassword />,
  },
  {
    path: "/vendor/create-password/:vendorId",
    component: <UpdatePassword />,
  },
  {
    path: "/vendor/setup/:vendorId",
    component: <UpdatePassword />,
  },
  {
    path: "/setpassword",
    component: <UpdatePassword />,
  },
  {
    path: "/setpassword/:token",
    component: <UpdatePassword />,
  },
  {
    path: "/vendorregistration/setpassword/:token",
    component: <UpdatePassword />,
  },
  {
    path: "/vendorRegistration/setpassword/:token",
    component: <UpdatePassword />,
  },
  {
    path: "/updatepassword",
    component: <UpdatePassword />,
  },
  {
    path: "/notifications",
    component: <Notification />,
  },
  {
    path: "/admin/reports/reminderhistory",
    component: <ReminderHistory />,
  },
  {
    path: "/admin/clients-pets",
    component: <ClientPets />,
  },
  {
    path: "/admin/customer-onboarding-form",
    component: <NewCustomerForm />,
  },
  {
    path: "/admin/clients-pets/importData",
    component: <HowToImport />,
  },
  {
    path: "/admin/clients-pets/google-contacts",
    component: <TakeGoogleData />,
  },
  {
    path: "/admin/clients-pets/import-excelData",
    component: <TakeExcelData />,
  },
  {
    path: "/admin/clients-pets/import-history",
    component: <ImportHistory />,
  },
  {
    path: "/search_clients",
    component: <ClientsSearch />,
  },
  {
    path: "/client-details/:id",
    component: <ClientDetailsPage />,
  },
  {
    path: "/admin/clients-pets/:id",
    component: <ViewClient />,
  },
  {
    path: "/clientchat",
    component: <ClientChat />,
  },
  {
    path: "/upgrade_plan",
    component: <Upgradeplanpage />,
  },
  {
    path: "/connectus",
    component: <Connectus />,
  },
  {
    path: "/pricing",
    component: <Pricingplans />,
  },
  {
    path: "/faqs",
    component: <Faqs />,
  },
  {
    path: "/Pricingpage",
    component: <Pricingpage />,
  },
  {
    path: "/book-demo",
    component: <BookDemo />,
  },
  {
    path: "/free-trial",
    component: <FreeTrial />,
  },
  {
    path: "/contact-us",
    component: <Contactus />,
  },
  {
    path: "/contactSales",
    component: <Contactsales />,
  },
  {
    path: "/contactCustomer",
    component: <Contactcustomer />,
  },
  {
    path: "/admin/getting-started",
    component: <GettingStarted />,
  },
  {
    path: "/freelistCustomer",
    component: <Freelistcustomer />,
  },
  {
    path: "/BusinessRegistration",
    component: <BusinessRegistration />,
  },
  {
    path: "/Addservice",
    component: <Addservice />,
  },
  {
    path: "/sidebar",
    component: <Sidebar />,
  },
  //service-management
  {
    path: "/admin/service-management/addon_service",
    component: <AddonServices />,
  },
  {
    path: "/admin/service-management/grooming",
    component: <ListServices />,
  },
  {
    path: "/admin/service-management/boarding",
    component: <RoomServices />,
  },
  {
    path: "/admin/service-management/dog_walking",
    component: <DogWakingService />,
  },
  {
    path: "/admin/service-management/vet_clinic",
    component: <VetClinicManagementTabs />,
  },
  {
    path: "/admin/service-management/daycare",
    component: <DayCareServices />,
  },
  {
    path: "/chatwithcustomer",
    component: <Chat />,
  },
  {
    path: "/adminDashboard",
    component: <AdminDashboard />,
  },

  //reports
  {
    path: "/admin/reports/standard",
    component: <StandardReportListing />,
  },
  {
    path: "/admin/reports/feedback",
    component: <Feedback />,
  },
  {
    path: "/admin/invoicePreview/:formatType/:orderId",
    component: <InvoicePreview />,
  },

  {
    path: "/admin/reports/custom",
    component: <CustomReportsListing />,
  },
  {
    path: "/admin/reports/standard/:reportType",
    component: <div style={{ padding: 20 }}>Report Details - Coming Soon</div>,
  },
  // settings
  {
    path: "/admin/settings/general_settings",
    // component: <MainPage />,
    component: <ProfileSettings />,
  },
  {
    path: "/admin/settings/online_profile",
    component: <MainPage />,
  },
  {
    path: "/admin/settings/size_management",
    component: <SizeManagement />,
  },
  {
    path: "/admin/settings/coat_management",
    component: <CoatManagement />,
  },
  {
    path: "/admin/settings/promotions_and_coupons",
    component: <PromotionsAndCoupons />,
  },
  {
    path: "/admin/discounts",
    component: <DiscountList />,
  },
  {
    path: "/admin/settings/discounts",
    component: <Navigate to="/admin/discounts" replace />,
  },
  {
    path: "/admin/forms",
    component: <Forms />,
  },
  {
    path: "/admin/discounts/create",
    component: <DiscountCreate />,
  },
  {
    path: "/admin/room_management",
    component: <RoomList />,
  },
  {
    path: "/admin/settings/store_management",
    component: <StoreList />,
  },
  {
    path: "/admin/settings/service_amenities",
    component: <ServiceAmenities />,
  },
  {
    path: "/client_pets/excelData-preview",
    component: <ExcelDataPreview />,
  },
  {
    path: "/customerList",
    component: <CustomerList />,
  },
  {
    path: "/forget-Password",
    component: <Forgetpassword />,
  },
  {
    path: "/viewCustomer/:id",
    component: <ViewCustomer />,
  },
  {
    path: "/updateCustomer/:id",
    component: <UpdateCustomer />,
  },
  {
    path: "/bookingList",
    component: <BookingList />,
  },
  {
    path: "/viewBooking/:id",
    component: <ViewBooking />,
  },
  {
    path: "/admin/staff-management/employees",
    component: <EmployeeList />,
  },
  {
    path: "/admin/staff-management/employees/:id",
    component: <EmployeeDetails />,
  },
  {
    path: "/create_employee",
    component: <EmployeeForm />,
  },
  {
    path: "/update_employee/:_id",
    component: <EmployeeForm />,
  },
  {
    path: "/orderList/addorder",
    component: <Orders />,
  },
  // edit
  {
    path: "/orderList/addorder/:id",
    component: <Orders />,
  },
  // get all order routes
  {
    path: "/orderList",
    component: <OrderLists />,
  },
  // /orderList/details/${order._id}
  {
    path: "/orderList/details/:orderId",
    component: <OrderListsDetail />,
  },
  {
    path: "/invoiceList",
    component: <InvoiceList />,
  },
  {
    path: "/invoices/:invoiceToken",
    component: <InvoiceLink />,
  },
  {
    path: "admin/order-management/enquiry",
    component: <EnquiryOrders />,
  },
  {
    path: "/admin/booking-management/online-appointments",
    component: <OnlineAppointment />,
  },
  {
    path: "/admin/booking-management/checkout-bookings",
    component: <CheckoutBookings />,
  },
  {
    path: "/admin/booking-management/listing",
    component: <ListingBookings />,
  },
  {
    path: "/admin/booking-management/calendar",
    component: <CalendarBookings />,
  },
  {
    path: "/admin/booking-management/boarding-activity",
    component: <TodaysBoardingActivity />,
  },

  {
    path: "admin/order-management/orders",
    component: <OrderManagementOrders />,
  },
  {
    path: "admin/order-management/payments",
    component: <Payments />,
  },
  //admin expense management
  {
    path: "admin/expense-management/expenses",
    component: <ExpenseManagement />,
  },
  {
    path: "admin/expense-management/payments",
    component: <ExpensePayments />,
  },
  //invendtory routes
  {
    path: "admin/inventory-management/purchase_bills",
    component: <PurchaseBills />,
  },
  {
    path: "admin/inventory-management/overview/:supplierId",
    component: <SupplierOverview />,
  },
  {
    path: "admin/inventory-management/products",
    component: <ProductView />,
  },
  // {
  //   path: "admin/inventory-management/products",
  //   component: <Products />,
  // },
  {
    path: "/admin/inventory-management/products/:productId/:inventoryId",
    component: <ProductView />,
  },
  {
    path: "/admin/inventory-management/products/:productId",
    component: <ProductView />,
  },
  {
    path: "/admin/inventory-management/product-import",
    component: <ProductImport />,
  },
  {
    path: "/admin/inventory-management/product-import-preview",
    component: <ProductImportPreview />,
  },
  {
    path: "/admin/inventory-management/product-import-history",
    component: <ProductImportHistory />,
  },
  {
    path: "admin/inventory-management/suppliers",
    component: <Suppliers />,
  },
  {
    path: "admin/inventory-management/purchase_bills/new",
    component: <PurchaseBill />,
  },
  {
    path: "admin/inventory-management/purchase_bills/:id",
    component: <ViewPurchaseBill />,
  },
  {
    path: "/admin/inventory-management/suppliers/:supplierId/purchase_bills/:billId",
    component: <ViewPurchaseBill />,
  },
  {
    path: "/admin/inventory-management/assigned-inventory",
    component: <StaffProductAssignment />,
  },
  {
    path: "/faq",
    component: <FAQPage />,
  },
  {
    path: "/service_inclusions",
    component: <ServiceInclusions />,
  },
  {
    path: "/admin/sales_order",
    component: <SalesOrder />,
  },
  {
    path: "/pos",
    component: <POS />,
  },
  {
    path: "/invoice",
    component: <InvoicePage />,
  },
  {
    path: "/home",
    component: <HomePage />,
  },
  {
    path: "*",
    component: <PageNotFound />,
  },
];

const Loader = () => (
  <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>
);

const AllRoutes = () => {
  return (
    <>
      <Router>
        <ScrollToTop />
        <NetworkStatusHandler />
        {/* <PlanExpiryGuard /> */}
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* <Route
              path="/checkout/order-summary/:customerid/:bookingid"
              element={<OrderSummary />}
            /> */}
            <Route
              path="/checkout/payment-details/:checkoutid"
              element={<PaymentDetails />}
            />
            <Route
              path="/import-history"
              element={
                <Navigate to="/admin/clients-pets/import-history" replace />
              }
            />
            <Route path="/checkout/thankyou/:orderid" element={<Thankyou />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route
              path="/form/:vendorId/:formId"
              element={<ConsentFormPublic />}
            />
            <Route path="/:token" element={<Peviewpage />} />
            <Route
              path="/consentForm/:consentToken"
              element={<ConsentForm />}
            />
            

            {routes.map((item, index) => {
              // console.log(`[ROUTE MATCH] Checking ${item.path}`); // Moved outside the element prop
              return (
                <Route
                  key={index}
                  path={item.path}
                  element={
                    item.path === "/" ||
                    item.path === "/login" ||
                    item.path === "/vendorRegistration" ||
                    item.path === "/forget-password" ||
                    item.path === "/faq" ? (
                      <LoginPageIsLogedIn>{item.component}</LoginPageIsLogedIn>
                    ) : item.path.startsWith("/vendor/") ||
                      item.path === "/registers" ||
                      item.path === "/kennel-boarding-software" ||
                      item.path === "/pet-grooming-salon" ||
                      item.path === "/pet-shop" ||
                      item.path === "/mobile-grooming-van" ||
                      item.path === "/pet-veterinary-clinic" ||
                      item.path === "/pricing" ||
                      item.path === "/faqs" ||
                      item.path === "/features" ||
                      item.path === "/resources" ||
                      item.path === "/free-listing" ||
                      item.path === "/about" ||
                      item.path === "/blog" ||
                      item.path === "/privacy-policy" ||
                      item.path === "/term" ||
                      item.path === "/blog/:slug" ||
                      item.path === "/contact-us" ||
                      item.path === "/free-trial" ||
                      item.path === "/contactCustomer" ||
                      item.path === "/contactSales" ? (
                      item.component
                    ) : (
                      <ProtectedRoute>{item.component}</ProtectedRoute>
                    )
                  }
                />
              );
            })}
          </Routes>
        </Suspense>
      </Router>
    </>
  );
};

export default AllRoutes;
